import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "@/lib/bizday";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { HoursOverrideDialog, isHoursBlock } from "@/components/business/HoursOverrideDialog";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";

// Moves the existing booking (same staff, same branch). Server re-checks availability/hours and re-syncs the consultation.
export function RescheduleButton({ booking: b, compact }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ist = new Date(new Date(b.start_time).getTime() + 330 * 60000).toISOString();   // business time (IST), same offset the save uses
  const [date, setDate] = useState(ist.slice(0, 10));
  const [time, setTime] = useState(ist.slice(11, 16));
  const [hours, setHours] = useState(null);
  const mut = useMutation({
    mutationFn: (reason = "") => api.patch(`/bookings/${b.id}/reschedule`, null, { params: {
      start_time: dayjs(`${date}T${time}:00`).format("YYYY-MM-DDTHH:mm:ss+05:30"), hours_override_reason: reason } }),
    onSuccess: (r) => {
      ["bookings", "cal", "assignments", "staff-avail", "vet-my-queue", "vet-assignment-issues", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      if (r.data?.hours_warning) toast.warning(r.data.hours_warning.msg);
      toast.success("Appointment rescheduled"); setHours(null); setOpen(false);
    },
    onError: (e) => { if (isHoursBlock(e)) setHours(e.response.data.detail); else toast.error(apiErr(e)); },
  });
  if (!hasPerm(user, "bookings.edit") || ["cancelled", "no_show", "completed"].includes(b.status)) return null;
  return (
    <>
      <button title="Reschedule" onClick={() => setOpen(true)} data-testid={`reschedule-${compact ? "m-" : ""}${b.id}`}
        className={`${compact ? "p-2" : "p-1.5"} rounded-md text-indigo-700 hover:bg-indigo-50`}><CalendarClock className="h-4 w-4" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-sm" data-testid={`reschedule-dialog-${b.id}`}>
          <DialogHeader><DialogTitle>Reschedule · {b.pet_name || b.customer_name}</DialogTitle>
            <DialogDescription>{b.service_name} · currently {ist.slice(0, 10)} {ist.slice(11, 16)} IST. Staff and branch stay the same.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-date" />
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="reschedule-time" />
          </div>
          <Button onClick={() => mut.mutate("")} disabled={mut.isPending || !date || !time} data-testid="reschedule-save" className="w-full bg-slate-900 text-white">
            {mut.isPending ? "Saving…" : "Save new time"}</Button>
        </DialogContent>
      </Dialog>
      <HoursOverrideDialog key={hours ? "open" : "closed"} prompt={hours} busy={mut.isPending} onCancel={() => setHours(null)} onConfirm={(r) => mut.mutate(r)} />
    </>
  );
}
